import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Components
import Navigation from "./components/Navigation";
import HeroSection from "./components/HeroSection";
import ProblemSection from "./components/ProblemSection";
import MeetVeraSection from "./components/MeetVeraSection";
import FeaturesShowcaseSection from "./components/FeaturesShowcaseSection";
import AICouncilSection from "./components/AICouncilSection";
import AcademySection from "./components/AcademySection";
import LevelsSection from "./components/LevelsSection";
import WeeklyReportSection from "./components/WeeklyReportSection";
import SecuritySection from "./components/SecuritySection";
import PricingSection from "./components/PricingSection";
import DownloadSection from "./components/DownloadSection";
import Footer from "./components/Footer";

// SEO Component
const SEOHead = () => {
  useEffect(() => {
    // Title
    document.title = "Findell — AI Financial Coach | Credit Score, Net Worth, Debt Payoff & More";
    
    // Meta Description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content = "Findell is the AI-powered financial wellness app with credit score monitoring, net worth tracking, debt payoff tools, and personalized coaching from Vera, Rex & Nova. Transform your relationship with money.";

    // OG Tags
    const ogTags = [
      { property: 'og:title', content: 'Findell — AI Financial Coach | Credit Score, Net Worth & Debt Payoff' },
      { property: 'og:description', content: 'Findell is the AI-powered financial wellness app with credit score monitoring, net worth tracking, debt payoff tools, and personalized coaching. Transform your money.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: '/og-image.png' },
      { property: 'og:url', content: window.location.href },
    ];

    ogTags.forEach(({ property, content }) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Twitter Card
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Findell — AI Financial Coach | Credit Score, Net Worth & Debt Payoff' },
      { name: 'twitter:description', content: 'AI-powered financial wellness with credit score monitoring, net worth tracking, debt payoff tools, and personalized coaching.' },
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin;

    // SoftwareApplication JSON-LD Schema
    let jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (!jsonLd) {
      jsonLd = document.createElement('script');
      jsonLd.type = 'application/ld+json';
      document.head.appendChild(jsonLd);
    }
    jsonLd.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Findell",
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "iOS, Android",
      "description": "AI-powered financial wellness app with credit score monitoring, net worth tracking, debt payoff tools, and personalized coaching.",
      "offers": [
        {
          "@type": "Offer",
          "name": "Free",
          "price": "0",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "name": "Pro Monthly",
          "price": "9.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31"
        },
        {
          "@type": "Offer",
          "name": "Pro Annual",
          "price": "79.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31"
        },
        {
          "@type": "Offer",
          "name": "Premium Monthly",
          "price": "19.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31"
        },
        {
          "@type": "Offer",
          "name": "Premium Annual",
          "price": "149.99",
          "priceCurrency": "USD",
          "priceValidUntil": "2027-12-31"
        }
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "500"
      }
    });

  }, []);

  return null;
};

// Analytics placeholders
const AnalyticsScripts = () => {
  useEffect(() => {
    // Google Analytics 4 placeholder
    // TODO: Replace 'G-XXXXXXXXXX' with actual GA4 measurement ID
    /*
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'G-XXXXXXXXXX');
    
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX';
    document.head.appendChild(script);
    */

    // Meta Pixel placeholder
    // TODO: Replace with actual Meta Pixel ID
    /*
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', 'XXXXXXXXXXXXXXXX');
    fbq('track', 'PageView');
    */
  }, []);

  return null;
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <SEOHead />
      <AnalyticsScripts />
      
      <Navigation />
      
      <main>
        <HeroSection />
        <ProblemSection />
        <MeetVeraSection />
        <FeaturesShowcaseSection />
        <AICouncilSection />
        <AcademySection />
        <LevelsSection />
        <WeeklyReportSection />
        <SecuritySection />
        <PricingSection />
        <DownloadSection />
      </main>
      
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
